/* eslint-disable react/jsx-key */
import { Downgraded, useHookstate } from "@hookstate/core";
import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import React, { useState } from "react";
import { useLang } from "~/hooks/useLang";
import { formFields } from "~/features/Dispatch/interfaces/IDispatchFormBuilder";
import FieldAutocompleteRegistration from '~/components/FieldAutocompleteRegistration';
import FieldTextRegistration from '~/components/FieldTextRegistration';
import { State } from "@hookstate/core";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import ImageActionFormBuilder, { type injuryMarks } from "../ImageActionFormBuilder";
import useDispatchState from "~/features/Dispatch/stores/DispatchState";
import NumberField from "~/components/NumberField";


const datePickerLabelTruncateSx = {
  '& .MuiInputLabel-root': {
    maxWidth: 'calc(100% - 60px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiInputLabel-shrink': {
    maxWidth: 'calc(133% - 60px)',
  },
}

interface RenderFormFieldBuilderProps {
  readonly fields: State<formFields[]>;
}

// eslint-disable-next-line max-lines-per-function
export default function RenderFormFieldBuilder({ fields }: RenderFormFieldBuilderProps) {
  const { translate } = useLang();
  const { dispatch } = useDispatchState();
  const { status } = dispatch();
  function showFilters(props: { field: State<formFields> }) {
    const input = props.field;
    const typeField = {
      'string':
        <FieldTextRegistration
          value={input.get().value as string}
          onChangeValue={(value) => input.merge({ value: value })}
          label={String(input.name?.get())}
          disableFields={!Boolean(input.is_active?.get()) || status.value.id === 8}
          required={Boolean(input.is_required.get())}
        />,
      'text':
        <FieldTextRegistration
          value={input.get().value as string}
          onChangeValue={(value) => input.merge({ value: value })}
          label={String(input.name?.get())}
          disableFields={!Boolean(input.is_active?.get()) || status.value.id === 8}
          required={Boolean(input.is_required.get())}
          multiline={true}
          rows={10}
          height={92}
        />,
      'number':
        <FieldTextRegistration
          value={input.get().value as string}
          onChangeValue={(value) => {
            input.merge({ value: value })
          }}
          label={String(input.name?.get())}
          disableFields={!Boolean(input.is_active?.get()) || status.value.id === 8}
          required={Boolean(input.is_required.get())}
          type="number"
        />
      ,
      'float':
        <FieldTextRegistration
          value={input.get().value as string}
          onChangeValue={(value) => input.merge({ value: value })}
          label={String(input.name?.get())}
          disableFields={!Boolean(input.is_active?.get()) || status.value.id === 8}
          required={Boolean(input.is_required.get())}
          type="number"
          float={true}
        />
      ,
      'boolean':
        <FormControlLabel
          required={Boolean(input.is_required.get())}
          checked={Boolean(input.get().value)}
          control={<Checkbox disabled={false || status.value.id === 8} />}
          label={translate(input.name.get() as string)}
          labelPlacement="end"
          onChange={(event, value) => {
            input.merge({ value: Boolean(value) })
          }}
        />,
      'select':
        <FieldAutocompleteRegistration
          label={translate(input.name?.get() as string)}
          onValueChange={(value: any) => {
            input.merge({ value: value?.name ?? null });
          }}
          optionsList={
            input.options.get()?.split(';')?.map((optionName, index) => ({ id: `${index}`, name: optionName }))
          }
          valueId={input.get().value as string}
          isRequired={Boolean(input.is_required.get())}
          disabled={!input.is_active?.get() || status.value.id === 8}
          formBuilder={true}
        />,
      'body-markings':
        <ImageActionFormBuilder
          isRequired={Boolean(input.is_required.get())}
          disabled={!input.is_active?.get() || status.value.id === 8}
          label={translate(input.name?.get() as string)}
          onValueChange={(value: injuryMarks) => {
            input.merge({ value: value });
          }}
          value={input.get().value}
        />
      ,
      'date':
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'pt-br'}>
          <DateTimePicker
            disabled={!input.is_active?.get() || status.value.id === 8}
            disableFuture
            views={['day', 'month', 'year']}
            minDate={dayjs().subtract(110, 'years')}
            label={translate(input.name?.get() as string)}
            format="DD/MM/YYYY"
            value={input.get().type !== "body-markings" && input.get().value && input.get().value !== '' ? dayjs(input.get().value) : null}
            onChange={(newValue: Dayjs | null) => {
              input.merge({ value: dayjs(newValue).format('YYYY-MM-DD') })
            }}
            slotProps={{
              textField: {
                size: 'small',
                required: Boolean(input.is_required.get()),
                inputProps: {
                  placeholder: translate(`dd/mm/aaaa`)
                },
                sx: datePickerLabelTruncateSx,
              }
            }}
          />
        </LocalizationProvider>,
      'date_time':
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'pt-br'}>
          <DateTimePicker
            disabled={!input.is_active?.get() || status.value.id === 8}
            ampm={false}
            ampmInClock={false}
            disableFuture
            views={['day', 'month', 'year', 'hours', 'minutes']}
            minDate={dayjs().subtract(110, 'years')}
            label={translate(input.name?.get() as string)}
            format="DD/MM/YYYY HH:mm"
            value={input.get().type !== "body-markings" && input.get().value && input.get().value !== '' ? dayjs(input.get().value) : null}
            onChange={(newValue: Dayjs | null) => {
              input.merge({ value: dayjs(newValue).format() })
            }}
            slotProps={{
              textField: {
                size: 'small',
                required: Boolean(input.is_required.get()),
                inputProps: {
                  placeholder: translate(`DD/MM/AAAA HH:mm`)
                },
                sx: datePickerLabelTruncateSx,
              }
            }}
          />
        </LocalizationProvider>,
    }

    return typeField[input.type.get() ?? '']
  }
  return (
    <>
      {fields.map((item: State<formFields>) => {
        return (
          <Grid
            item
            xs={item.type.get() === 'text' ? 12 : item.type.get() === 'body-markings' ? 12 : 3}
            sx={{ padding: 1, height: item.type.get() === 'text' ? '300px' : 'auto' }}
          >
            {showFilters({ field: item })}
          </Grid>
        )
      }
      )}
    </>
  )
}